import { DefaultTheme } from 'styled-components';
import { FormState } from 'src/types';

import theme from 'src/theme/theme';

export const getIconSize = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return '13.33px';
    case 'medium':
      return '20px';
    case 'large':
      return '26.67px';
    default:
      return '20px';
  }
};
export const getPadding = (size: 'small' | 'medium' | 'large'): string => {
  if (size === 'small') {
    return '5.33px';
  } else if (size === 'medium') {
    return '10px';
  } else if (size === 'large') {
    return '14.67px';
  }
  return '0px';
};

export const getIconColor = (
  variant: 'primary' | 'secondary' | 'tertiary',
  disabled?: boolean,
) => {
  if (disabled) {
    return theme.newPalette.neutral.N500;
  }
  switch (variant) {
    case 'primary':
      return theme.newPalette.neutral.N0;
    case 'secondary':
      return theme.newPalette.neutral.N800;
    case 'tertiary':
      return theme.newPalette.neutral.N800;
    default:
      return theme.newPalette.neutral.N0;
  }
};

export const getFormHelpTextColor = (state: FormState, t: DefaultTheme) => {
  if (!t.isVaultVariant) {
    switch (state) {
      case FormState.SUCCESS:
        return t.atlas.semantic.color.status.success.text;
      case FormState.ERROR:
        return t.atlas.semantic.color.status.error.default;
      case FormState.WARNING:
        return t.atlas.semantic.color.status.warning.text;
      case FormState.DISABLED:
        return t.atlas.semantic.color.type.disabled;
      default:
        return t.atlas.semantic.color.type.muted;
    }
  }
  switch (state) {
    case FormState.SUCCESS:
      return theme.newPalette.turquoise.T400;
    case FormState.ERROR:
      return theme.newPalette.red.R300;
    case FormState.WARNING:
      return theme.newPalette.yellow.Y500;
    case FormState.DISABLED:
      return theme.newPalette.neutral.N300;
    default:
      return theme.newPalette.neutral.N600;
  }
};

export const getFormIconColor = (state: FormState, t?: DefaultTheme) => {
  if (t && !t.isVaultVariant) {
    switch (state) {
      case FormState.SUCCESS:
        return t.atlas.semantic.color.status.success.text;
      case FormState.ERROR:
        return t.atlas.semantic.color.status.error.default;
      case FormState.WARNING:
        return t.atlas.semantic.color.status.warning.text;
      case FormState.DISABLED:
        return t.atlas.semantic.color.type.disabled;
      case FormState.DEFAULT:
        return t.atlas.semantic.color.status.notification.text;
      default:
        return t.atlas.semantic.color.type.default;
    }
  }
  switch (state) {
    case FormState.SUCCESS:
      return theme.newPalette.turquoise.T300;
    case FormState.ERROR:
      return theme.newPalette.red.R200;
    case FormState.WARNING:
      return theme.newPalette.yellow.Y400;
    case FormState.DISABLED:
      return theme.newPalette.neutral.N300;
    case FormState.DEFAULT:
      return theme.newPalette.neutral.N900;
    default:
      return theme.newPalette.neutral.N600;
  }
};

export const getFormStateDisplayStyles = (
  state: FormState,
  t?: DefaultTheme,
) => {
  if (t && !t.isVaultVariant) {
    const s = t.atlas.semantic.color;
    switch (state) {
      case FormState.SUCCESS:
        return `
        border-color: ${s.status.success.text};
        background-color: ${s.status.success.background};
        `;
      case FormState.ERROR:
        return `
        border-color: ${s.status.error.default};
        background-color: ${s.status.error.background};
        `;
      case FormState.WARNING:
        return `
        border-color: ${s.status.warning.text};
        background-color: ${s.status.warning.background};
        `;
      case FormState.DISABLED:
      case FormState.READONLY:
      case FormState.INFO:
        return `
        border-color: ${s.outline.disabled};
        background-color: ${s.surface.subtle};
        `;
      default:
        return `
        border-color: ${s.outline.default};
        background-color: ${s.surface.default};
        `;
    }
  }
  switch (state) {
    case FormState.SUCCESS:
      return `
      border-color: ${theme.newPalette.turquoise.T400};
      background-color: ${theme.newPalette.turquoise.T100};

      &:focus {
        border: 1px solid ${theme.newPalette.turquoise.T400};
        outline: 3px solid ${theme.newPalette.turquoise.T100};
      }
      `;
    case FormState.ERROR:
      return `
      border-color: ${theme.newPalette.red.R400};
      background-color: ${theme.newPalette.red.R100};

      &:focus {
        border: 1px solid ${theme.newPalette.turquoise.T400};
        outline: 3px solid ${theme.newPalette.turquoise.T100};
      }
      `;
    case FormState.WARNING:
      return `
      border-color: ${theme.newPalette.yellow.Y400};
      background-color: ${theme.newPalette.yellow.Y100};

      &:focus {
        border: 1px solid ${theme.newPalette.turquoise.T400};
        outline: 3px solid ${theme.newPalette.turquoise.T100};
      }
      `;
    case FormState.DISABLED:
      return `
      border-color: ${theme.newPalette.neutral.N300};
      background-color: ${theme.newPalette.neutral.N100};

      &:focus {
        outline: none;
      }
      `;
    case FormState.READONLY:
      return `
      border-color: ${theme.newPalette.neutral.N300};
      background-color: ${theme.newPalette.neutral.N100};

      &:focus {
        outline: none;
      }
      `;
    case FormState.INFO:
      return `
      border-color: ${theme.newPalette.neutral.N300};
      background-color: ${theme.newPalette.neutral.N100};

      &:focus {
        outline: none;
      }
      `;
    default:
      return `
      border-color: ${theme.newPalette.neutral.N400};
      background-color: ${theme.newPalette.neutral.N0};

      &:focus {
        border: 1px solid ${theme.newPalette.turquoise.T400};
        outline: 3px solid ${theme.newPalette.turquoise.T100};
      }
      `;
  }
};
